import React from "react";
import { Card } from "./ui/card";
import { ShoppingCart, Package, TrendingUp, Users, Star, Zap } from "lucide-react";

const Ecommerce = () => {
  const features = [
    {
      icon: <ShoppingCart className="h-8 w-8 text-green-600" />,
      title: "Ventas automatizadas",
      description: "Chatbots inteligentes que guían a tus clientes desde la consulta hasta la compra, aumentando las conversiones y reduciendo el abandono del carrito.",
      subtitle: "Disponibles 24/7 para no perder ninguna venta"
    },
    {
      icon: <Package className="h-8 w-8 text-blue-600" />,
      title: "Gestión inteligente de inventario",
      description: "Sistemas que predicen la demanda, optimizan el stock y alertan automáticamente sobre productos agotados o con exceso de inventario.",
      subtitle: "Evita roturas de stock y optimiza tu capital"
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-purple-600" />,
      title: "Recomendaciones personalizadas",
      description: "IA que analiza el comportamiento de compra para sugerir productos relevantes, aumentando el valor promedio del carrito y la satisfacción del cliente.",
      subtitle: "Aumenta las ventas cruzadas y la fidelización"
    },
    {
      icon: <Users className="h-8 w-8 text-orange-600" />,
      title: "Marketing automatizado",
      description: "Campañas de email inteligentes, seguimiento de leads y remarketing automático basado en el comportamiento real de tus clientes.",
      subtitle: "Convierte visitantes en clientes recurrentes"
    }
  ];

  const platforms = [
    { name: "Shopify", logo: "🛍️" },
    { name: "PrestaShop", logo: "🛒" },
    { name: "WooCommerce", logo: "🛒" },
    { name: "Magento", logo: "🛒" },
    { name: "BigCommerce", logo: "🛒" },
    { name: "OpenCart", logo: "🛒" }
  ];

  return (
    <section id="ecommerce" className="py-20 lg:py-32 bg-gradient-to-br from-green-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 border border-green-200 rounded-full text-sm text-green-600 mb-6">
            <Star className="w-4 h-4 mr-2" />
            Especialistas en Ecommerce
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-800 mb-6">
            Automatiza tu{" "}
            <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              tienda online
            </span>
          </h2>
          
          <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Transforma tu ecommerce con IA que gestiona inventario, atiende clientes y optimiza 
            ventas mientras tú te enfocas en hacer crecer tu negocio.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <Card 
              key={index}
              className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:transform hover:scale-105 group"
            >
              {/* Decorative elements */}
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-300"></div>
                <div className="relative bg-white p-4 rounded-2xl shadow-sm">
                  {feature.icon}
                  <div className="absolute top-2 right-2">
                    <Zap className="h-4 w-4 text-green-300" />
                  </div>
                  <div className="absolute bottom-2 left-2">
                    <Star className="h-3 w-3 text-blue-300" />
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-green-600 transition-colors">
                {feature.title}
              </h3>
              
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {feature.description}
              </p>
              
              <p className="text-gray-500 text-sm italic font-medium">
                {feature.subtitle}
              </p>
            </Card>
          ))}
        </div>

        {/* Platforms Integration */}
        <div className="text-center">
          <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-8">
            Compatible con las principales plataformas
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {platforms.map((platform, index) => (
              <div 
                key={index} 
                className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 text-center"
              >
                <div className="text-4xl mb-3">{platform.logo}</div>
                <h4 className="font-semibold text-gray-800 group-hover:text-green-600 transition-colors">
                  {platform.name}
                </h4>
              </div>
            ))}
          </div>
          
          <p className="text-gray-500 mt-8 text-lg">
            Y muchas más plataformas de ecommerce
          </p>
        </div>
      </div>
    </section>
  );
};

export default Ecommerce;
